import { storeWrapper } from '../storeWrapper'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { ospreyClientSchema } from '@repo/validation/schema_ospreyAPI'
import { debounce, throttle } from '@repo/utils/debounce'

const initialState = {
  clients: {},
  currentClientNo: null,
  currentClient: null,
  clientSearchResults: 0,
  fetchingClientSearchResults: false,
  suggestedClients: []
}

export const useClientStore = storeWrapper('useClientStore', (set, get) => ({
  ...initialState,
  fetchClient: async ({ clientNo, ospreyClient_DI }) => {
    clientNo = clientNo ? clientNo.trim() : clientNo

    debounce({
      id: `fetchClient-${clientNo}`,
      delay: 200,
      callback: async () => {
        let client

        if (ospreyClient_DI) {
          client = ospreyClient_DI({ clientNo })
        } else {
          client = await ospreyAPI({
            service: 'clients',
            endpoint: 'Client/GetClientWithNamedClientAndClientPhones',
            data: { clientNo },
            schema: ospreyClientSchema
          })
        }

        const existingClients = get().clients
        existingClients[clientNo] = client
        set({ clients: { ...existingClients } })

        if (clientNo === get().currentClientNo) {
          set({ currentClient: client })
        }
      }
    })
  },
  searchClients: async ({ searchCriteria = '' }) => {
    throttle({
      id: `searchClients`,
      callback: async () => {
        set({ fetchingClientSearchResults: true })

        const { rows, totalRowCount } = await ospreyAPI({
          service: 'clients',
          endpoint: 'Client/GetAllAbbreviatedWithNameBySearchTerm',
          data: {
            SearchTerm: searchCriteria,
            StartAtIndex: 0,
            PageSize: 60
          }
        })

        const clientSearchResults = rows.map((client) => {
          const { forename, surname, emailAddress, clientNo } = client
          const clientName =
            forename && surname ? `${client.forename} ${client.surname}` : client.surname || 'No name found'

          return {
            ...client,
            clientName,
            searchTerm: `${clientName} ${emailAddress || ''} ${clientNo}`
          }
        })

        set({ clientSearchResults, totalClientSearchResults: totalRowCount, fetchingClientSearchResults: false })
      }
    })
  },
  setCurrentClient: ({ clientNo, ospreyClient_DI }) => {
    clientNo = clientNo ? clientNo.trim() : clientNo
    const currentClient = get().clients[clientNo] || null
    set({ currentClientNo: clientNo, currentClient })

    if (clientNo || ospreyClient_DI) get().fetchClient({ clientNo, ospreyClient_DI })
  },
  fetchNextClientNo: async ({ surname }) => {
    // already debounced in the UI
    const nextClientNo = await ospreyAPI({
      service: 'clients',
      endpoint: 'Client/GetNextClientNo',
      data: { surname }
    })

    return Promise.resolve(nextClientNo)
  },
  checkIfClientNoExists: async ({ clientNo }) => {
    // already debounced in the UI
    const res = await ospreyAPI({
      service: 'clients',
      endpoint: 'Client/Exists',
      data: { id: clientNo }
    })

    return Promise.resolve(res === true)
  },
  checkforSimilarClients: async ({
    forename,
    surname,
    house,
    area,
    postalTown,
    county,
    postcode,
    startAtIndex = 0,
    pageSize = 20
  }) => {
    const res = await ospreyAPI({
      service: 'clients',
      endpoint: 'Client/GetAllAbbreviatedWithName',
      data: {
        Forename: forename || null,
        Surname: surname || null,
        House: house || null,
        Area: area || null,
        PostalTown: postalTown || null,
        Postcode: postcode || null,
        County: county || null,
        StartAtIndex: startAtIndex,
        PageSize: pageSize
      }
    })

    return Promise.resolve(res)
  },
  fetchSuggestedClients: async (namedClients) => {
    return new Promise((resolve) => {
      debounce({
        id: 'fetchSuggestedClients',
        delay: 500,
        callback: async () => {
          let fetchedSuggestedClients = []
          const fetchedClientNumbers = []

          // namedClients is an array of namedClient objects, e.g. [{forename, surname, emailAddress, ...rest}]
          // we need to call the endpoint for each named client

          try {
            for (const { forename, surname, emailAddress } of namedClients) {
              const suggestions = await ospreyAPI({
                service: 'clients',
                endpoint: 'Client/GetAllAbbreviatedWithNameBySearchTerms',
                data: { Forename: forename ?? null, Surname: surname ?? null, EmailAddress: emailAddress ?? null }
              })

              // there might be duplicates between calls so we make sure we only add unique results to our array
              suggestions.forEach((suggestion) => {
                // if we've already added this client to the array we can skip it
                if (fetchedClientNumbers.includes(suggestion.clientNo)) return

                // otherwise, we add it to the array and the fetchedSuggestedClients array
                fetchedClientNumbers.push(suggestion.clientNo)
                fetchedSuggestedClients.push(suggestion)
              })
            }
          } catch (error) {
            console.error('Error fetching suggested clients:', error)
          }

          // finally, we need to sort the fetchedSuggestedClients array by rank (but we only meed to do this if there is more than one namedClient)
          if (namedClients.length > 1) {
            fetchedSuggestedClients = fetchedSuggestedClients.sort((a, b) => {
              if (a.rank > b.rank) return -1
              if (a.rank < b.rank) return 1
              return 0
            })
          }

          set({ suggestedClients: fetchedSuggestedClients })
          resolve(fetchedSuggestedClients)
        }
      })
    })
  },
  reset: () => {
    const clients = get().clients
    Object.keys(clients).forEach((key) => delete clients[key])
    set({ ...initialState, clients })
  }
}))
